import {
  css,
  customElement,
  property,
  PropertyValues,
  queryAssignedNodes,
  TemplateResult,
  unsafeCSS,
} from 'lit-element';
import { html } from 'lit-html';

import style from './slider-scale.component.scss';
import { hostStyles } from '../../../host.styles';
import { countDecimals, format } from '../../../utils/format.utils';
import { classMap } from 'lit-html/directives/class-map';
import { getOffsetForSliderByInterval } from '../slider.utils';
import { state } from 'lit-element/lib/decorators';
import { SliderBaseClass } from '../slider-base.class';
import { SliderTickLabel } from '../slider-tick-label/slider-tick-label.component';

const sliderStyles = css`
  ${unsafeCSS(style)}
`;

/**
 * The slider scale includes all the logic and styling for the scale and should always be used along the
 * zui-slder-basic component in the wrapping zui-slider component.
 *
 * **Important note 1**: The slider only works properly if the step value is  integer divisible with regard to the
 *  minimum / maximum value
 *
 * **Important note 2**: The usage of the label format can be looked up here: https://github.com/alexei/sprintf.js/
 *  some examples:
 *
 * - "%s" for strings
 * - "%.2f" for floats with a certain amount of decimal places
 * - "%f" for pure floats
 * - "+%.2f%%" for having a "+" as prefix and "%" as suffix
 *
 * **Important note 3**: When you change the `value` via code you have to make sure that the value
 * is compatible with `min`, `max` and `step` on your own.
 * The component will not prevent you from setting invalid values via code but the visual behaviour will likely break.
 *
 * @example
 *
 * ```html
 *  <zui-slider-scale
 *    min="0"
 *    max="10"
 *    value="0"
 *    step="1"
 *    active-line-start="3"
 *    tick-interval="0.5"
 *    label-interval="1"
 *    label-format="$ %.2f"
 *    readonly
 *    >
 *      <zui-slider-tick-label>one</<zui-slider-tick-label>
 *      <zui-slider-tick-label>two</<zui-slider-tick-label>
 *    </zui-slider-scale>
 * ```
 *
 * @slot - The default slot, used for the custom tick labels
 */
@customElement('zui-slider-scale')
export class SliderScale extends SliderBaseClass {
  static readonly styles = [hostStyles, sliderStyles];

  /**
   * the tick interval for the slider
   */
  @property({ reflect: true, type: Number, attribute: 'tick-interval' })
  tickInterval = 0;

  /**
   * the label interval for the slider
   */
  @property({ reflect: true, type: Number, attribute: 'label-interval' })
  labelInterval = 0;

  /**
   * the format template of the label. Use https://github.com/alexei/sprintf.js/ as a reference.
   */
  @property({ reflect: true, type: String, attribute: 'label-format' })
  labelFormat;

  @queryAssignedNodes('', true, 'zui-slider-tick-label')
  private _customTickLabelElements: SliderTickLabel[];

  // returns the format string template, used for the labels
  private get _derivedLabelFormat(): string {
    // when there is no label format set at all, set it to a float number with the label interval given decimal places
    if (this.labelFormat === undefined) {
      return `%.${countDecimals(this.labelInterval)}f`;
    }

    return this.labelFormat;
  }

  @state()
  private _customLabels: string[] = [];

  // returns the final array of labels, containing as much custom labels as slotted
  private get _derivedTickLabelArray(): string[] {
    return this._calcTickAndLabelArray(this.labelInterval).map((label, labelIndex) =>
      this._customLabels[labelIndex]
        ? this._getFormattedLabel(this._customLabels[labelIndex], label)
        : this._getFormattedLabel(this._derivedLabelFormat, label)
    );
  }

  private _getFormattedLabel(template, value): string {
    let label = value;
    try {
      label = format(template, value);
    } catch (error) {
      console.warn('error formatting template string: ', error);
    }
    return label;
  }

  /**
   * Creates an array of generic ticks and labels
   *
   * @param interval the interval that is used for calculation
   *
   * @returns {Array} array of ticks and labels
   */
  private _calcTickAndLabelArray(interval: number): number[] {
    if (interval === 0) {
      return [];
    }

    const normalizedRange = this.max - this.min;

    if (!Number.isInteger(normalizedRange / Number(this.tickInterval))) {
      console.warn('Tick interval is not integer divisible with regard to the min-max range!');
    }

    // we have to add an extra step for the zero
    const length = Math.floor(normalizedRange / Number(interval)) + 1;

    return Array.from({ length }, (v, label) => {
      label = this.min + label * this.labelInterval;

      // kill the rounding glitches!
      return Math.round(label * 100) / 100;
    });
  }

  private _handleSlotChange(): void {
    this._customLabels = this._customTickLabelElements.map((label) => label.textContent);
  }

  protected updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    // for every change on either tickInterval or labelInterval we need to re-calculate the offset
    // that is needed to pad the ticks / labels
    if (changedProperties.has('tickInterval')) {
      this.style.setProperty(
        '--zui-slider-tick-odd-offset',
        `${getOffsetForSliderByInterval(this.max - this.min, this.tickInterval)}%`
      );
    }
    if (changedProperties.has('labelInterval')) {
      this.style.setProperty(
        '--zui-slider-label-odd-offset',
        `${getOffsetForSliderByInterval(this.max - this.min, this.labelInterval)}%`
      );
    }
  }

  protected render(): TemplateResult {
    return html` <div class="scale-container">
      <div id="tick-container">
        ${this._calcTickAndLabelArray(this.tickInterval).map(() => html`<div class="tick"></div>`)}
      </div>
      <div id="label-container">
        <slot @slotchange="${this._handleSlotChange}"></slot>
        ${this._derivedTickLabelArray.map((tickLabel, currentIndex, tickLabels) => {
          // we're using an invisible helper tick to align the label properly and add the
          // decimal places in the template, which makes the handling easier in this case

          const isFirstLabel = currentIndex === 0;
          const isLastLabel = currentIndex === tickLabels.length - 1;
          // the required font will exceed the track after more than four chars
          const longLabel = tickLabel ? tickLabel.length >= 4 : false;
          const hasNoCustomPadding = !getComputedStyle(this).getPropertyValue('--zui-slider-padding');

          return html` <div
            class="${classMap({
              tick: true,
              first: isFirstLabel && longLabel && hasNoCustomPadding,
              last: isLastLabel && longLabel && hasNoCustomPadding,
            })}"
            style=" --zui-slider-tick-width: 0;"
          >
            <span
              class="${classMap({
                label: true,
                first: isFirstLabel && longLabel && hasNoCustomPadding,
                last: isLastLabel && longLabel && hasNoCustomPadding,
              })}"
            >
              ${tickLabel}
            </span>
          </div>`;
        })}
      </div>
    </div>`;
  }
}
